import { create } from 'zustand';

/**
 * Admin session state.
 *
 * Like the customer web app, **no token is persisted to storage** — the access
 * token lives in memory only and the refresh token is an httpOnly cookie
 * (`projoint_admin_rt`, scoped per-app so it doesn't collide with the web app's
 * `projoint_rt` on the shared API origin).  On reload the access token is
 * re-minted by a silent `POST /api/auth/refresh` against the cookie (see the
 * root route's `beforeLoad`).  Nothing here touches localStorage, so an XSS
 * can't read the session.
 */
interface AuthState {
  accessToken: string | null;
  /** Wall-clock ms when the access token expires (informational; refresh is
   *  driven by 401s, not a timer). */
  accessExpiresAt: number | null;
  /** Set after a successful /api/me with isSuperadmin=true. */
  isAuthenticated: boolean;
  setTokens: (input: { accessToken: string; accessExpiresAt: number }) => void;
  setAuthenticated: (v: boolean) => void;
  clear: () => void;
}

export const useAuthStore = create<AuthState>()((set) => ({
  accessToken: null,
  accessExpiresAt: null,
  isAuthenticated: false,
  setTokens: ({ accessToken, accessExpiresAt }) => set({ accessToken, accessExpiresAt }),
  setAuthenticated: (v) => set({ isAuthenticated: v }),
  clear: () => set({ accessToken: null, accessExpiresAt: null, isAuthenticated: false }),
}));

export const getAccessToken = (): string | null => useAuthStore.getState().accessToken;
